import type { Dispatch, SetStateAction } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useCookie } from 'react-use'
import Debug from 'debug'
import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'

import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { ConsentRequestSchema } from '@jotta/grpc-connect-openapi/consent'
import { consent as consentDescriptor } from '@jotta/grpc-connect-openapi/consentQuery'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import SvgCaretDown from '@jotta/ui/SvgCaretDown'
import SvgCaretRight from '@jotta/ui/SvgCaretRight'
import SvgLogoMain from '@jotta/ui/SvgLogo_mainLight'
import { ToggleButton } from '@jotta/ui/ToggleButton'
import { cookies } from '@jotta/utils/cookie'
import { resolveVisitorId } from '@jotta/utils/matomo'

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Dialogs/Dialog/Dialog'

import type {
  Consent,
  CookieCategory,
  CookieDeclaration,
} from './cookieConsent'
import {
  categories,
  categoryDescription,
  categoryMap,
  categoryTitle,
  cookieIgnoreList,
  useCookieConsentStore,
  useCookieDeclarations,
} from './cookieConsent'
import { decodeLegacyConsentCookie } from './legacyCookieConsent'

const debug = Debug('CookieConsentModal')

declare global {
  interface Window {
    _mtm?: any[]
    _paq?: any[]
    gtag: ((action: string, context: string, params: any) => void) | undefined
  }
}

function shouldTrigger({
  statisticalCookieConsent,
  marketingCookieConsent,
}: Consent) {
  return [statisticalCookieConsent, marketingCookieConsent].some(
    value => value !== undefined,
  )
}

function MainPage() {
  return (
    <>
      <p>
        <Trans>
          We use cookies to improve and keep all Jottacloud services secure,
          customize and measure marketing, content and communications.
        </Trans>
      </p>
      <p>
        <Trans>
          You can check and change which data we process at any time in the
          cookie settings and you can read more in-depth about this in our&nbsp;
          <a className="link" href="/cookies" target="_blank">
            cookie policy
          </a>
        </Trans>
      </p>
    </>
  )
}

function Category({
  category,
  cookies,
  consent,
  updateConsent,
}: {
  category: CookieCategory
  cookies: CookieDeclaration[]
  consent: boolean | undefined
  updateConsent: (category: CookieCategory, consented: boolean) => void
}) {
  const [expanded, setExpanded] = useState(false)

  if (cookies.length === 0) {
    return undefined
  }

  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr),auto] items-start gap-x-3 gap-y-4 sm:grid-cols-[10rem,minmax(0,1fr),auto] sm:gap-4">
        <Button
          icon={expanded ? SvgCaretDown : SvgCaretRight}
          onClick={() => setExpanded(!expanded)}
          className="btn btn-ghost label-md flex items-center justify-start"
        >
          {categoryTitle(category)}
        </Button>
        <div className="col-start-2 row-start-1 justify-self-end sm:col-start-3">
          {category === 'necessary' ? (
            <div aria-hidden="true" className="h-8 w-14" />
          ) : (
            <ToggleButton
              checked={consent}
              onCheckedChange={a => updateConsent(category, a)}
            />
          )}
        </div>
        <div className="col-span-2 row-start-2 min-w-0 break-words sm:col-span-1 sm:col-start-2 sm:row-start-1">
          {categoryDescription(category)}
        </div>
      </div>

      {expanded && (
        <div className="ml-2 border-b border-light pb-2">
          {cookies.map(
            ({
              data_processor_name,
              name,
              domain,
              description,
              expiration,
              data_processor_privacy_policy,
            }) => (
              <dl
                key={name}
                className="mt-4 grid grid-cols-1 items-center gap-x-3 border-t border-light pt-4 md:grid-cols-[auto,1fr]"
              >
                <dt className="label-sm">
                  <Trans>Service</Trans>
                </dt>
                <dd className="label-md">{data_processor_name}</dd>
                <dt className="label-sm">
                  <Trans>Purpose</Trans>
                </dt>
                <dd>{description}</dd>
                <dt className="label-sm">
                  <Trans>Privacy Policy</Trans>
                </dt>
                <dd className="break-all">
                  <a className="link" href={data_processor_privacy_policy}>
                    {data_processor_privacy_policy}
                  </a>
                </dd>
                <dt className="label-sm">
                  <Trans>Name</Trans>
                </dt>
                <dd>{name}</dd>
                <dt className="label-sm">
                  <Trans>Expiration</Trans>
                </dt>
                <dd>{expiration}</dd>
                <dt className="label-sm">
                  <Trans>Vendor</Trans>
                </dt>
                <dd>{domain}</dd>
              </dl>
            ),
          )}
        </div>
      )}
    </div>
  )
}

function Settings({
  cookieDeclarations,
  consent,
  updateConsent,
}: {
  cookieDeclarations?: CookieDeclaration[]
  consent: Consent | undefined
  updateConsent: (category: CookieCategory, consented: boolean) => void
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      {categories.map(category => {
        const categoryCookies = cookieDeclarations?.filter(
          c => c.category === category,
        )

        if (!categoryCookies) {
          return null
        }

        return (
          <Category
            key={category}
            category={category}
            cookies={categoryCookies}
            consent={consent && consent[categoryMap[category]]}
            updateConsent={updateConsent}
          />
        )
      })}
    </div>
  )
}

function matomoPush(event: any) {
  try {
    debug('matomo tag event', event)
    window._mtm?.push(event)
  } catch (e) {
    reportError(e)
  }
}
function matomoPushTracker(eventName: string) {
  try {
    debug('matomo tracker event', eventName)
    window._paq?.push([eventName])
  } catch (e) {
    reportError(e)
  }
}

function triggerDefaults() {
  // https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced#default-consent
  debug('gtag default')
  try {
    window.gtag?.call(window.gtag, 'consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    })
  } catch (e) {
    reportError(e)
  }
}

function triggerEvent(consent: Consent, cookieConsentNeeded: boolean) {
  const {
    functionalCookieConsent,
    statisticalCookieConsent,
    marketingCookieConsent,
  } = consent

  // Trigger Google consent first, since Google consent needs to be given once Matomo triggers possible Google conversion
  // https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced#update-consent
  const gtagParameters = {
    ad_storage: marketingCookieConsent ? 'granted' : 'denied',
    ad_user_data: marketingCookieConsent ? 'granted' : 'denied',
    ad_personalization: marketingCookieConsent ? 'granted' : 'denied',
    analytics_storage: statisticalCookieConsent ? 'granted' : 'denied',
  }
  debug('gtag update', gtagParameters)
  try {
    window.gtag?.call(window.gtag, 'consent', 'update', gtagParameters)
  } catch (e) {
    reportError(e)
  }

  if (functionalCookieConsent) {
    matomoPush({
      event: 'cookie_cat_functional',
    })
  }
  if (statisticalCookieConsent) {
    matomoPush({
      event: 'cookie_cat_statistic',
    })
  }
  if (marketingCookieConsent) {
    matomoPush({
      event: 'cookie_cat_marketing',
    })
  }
  matomoPush({
    event: 'cookie_consent',
    cookie_cat_functional: functionalCookieConsent,
    cookie_cat_statistic: statisticalCookieConsent,
    cookie_cat_marketing: marketingCookieConsent,
  })

  if (statisticalCookieConsent) {
    matomoPushTracker('setConsentGiven')
    if (cookieConsentNeeded) {
      matomoPush({
        event: 'enable_analytics',
      })
    }
  }
  if (!statisticalCookieConsent && resolveVisitorId()) {
    matomoPushTracker('requireConsent')
  }
}

export const CookieConsentContext = createContext({
  container: undefined as Element | undefined,
  cookieConsentNeeded: true,
  disableUnconsentedCookieDetection: false,
  requestCookieConsent: () => {},
})

export function CookieConsentModal({
  openState,
  disableBlur,
}: {
  openState?: [boolean, Dispatch<SetStateAction<boolean>>]
  disableBlur?: boolean
}) {
  const [open, setOpen] = openState || [false, undefined]
  const { data: cookieDeclarations } = useCookieDeclarations()
  const [consent, setConsent] = useCookieConsentStore()
  const [customizedConsent, setCustomizedConsent] = useState(consent)
  const [displaySettings, setDisplaySettings] = useState(false)
  const { container, cookieConsentNeeded, disableUnconsentedCookieDetection } =
    useContext(CookieConsentContext)

  useEffect(triggerDefaults)

  useEffect(() => {
    setCustomizedConsent(consent)
    if (consent && shouldTrigger(consent)) {
      triggerEvent(consent, cookieConsentNeeded)
    }
  }, [consent, cookieConsentNeeded])

  const { mutate, isPending } = useMutation(consentDescriptor, {
    throwOnError: false,
    transport: publicTransport,
    onSettled: (response, error, request) => {
      setDisplaySettings(false)
      if (setOpen) {
        setOpen(false)
      }
      const persistedConsent = response
        ? request
        : {
            // we encountered an error and don't want to bother the user anymore.
            // store a conservative "consent" locally
            url: request.url,
          }
      setConsent(create(ConsentRequestSchema, persistedConsent))
      if (error) {
        reportError(error)
      }
    },
  })
  const persistConsent = useCallback(
    (newConsent: Consent) => {
      mutate({
        ...newConsent,
        clientUserId: newConsent.clientUserId || crypto.randomUUID(),
        url: location.href,
      })
    },
    [mutate],
  )
  const updateCustomizedConsent = useCallback(
    (category: CookieCategory, consented: boolean) => {
      setCustomizedConsent(prevState =>
        create(ConsentRequestSchema, {
          ...prevState,
          [categoryMap[category]]: consented,
        }),
      )
    },
    [setCustomizedConsent],
  )

  // report unrecognized and unconsented cookies
  useEffect(() => {
    if (cookieDeclarations && !disableUnconsentedCookieDetection) {
      const cookieNames = Object.keys(cookies.getAll())
      const unrecognizedCookies: string[] = []
      const unconsentedCookies: string[] = []
      for (const cookieName of cookieNames) {
        if (cookieIgnoreList.includes(cookieName)) {
          continue
        }
        const declaration = cookieDeclarations.find(
          ({ name, regex }) => name === cookieName || regex?.test(cookieName),
        )
        if (!declaration) {
          unrecognizedCookies.push(cookieName)
          continue
        }
        const category = declaration.category
        if (
          category !== 'necessary' &&
          (!consent || !consent[categoryMap[category]])
        ) {
          unconsentedCookies.push(cookieName)
        }
      }
      if (unrecognizedCookies.length) {
        reportError(`unrecognized cookies: ${unrecognizedCookies}`)
      }
      if (unconsentedCookies.length) {
        reportError(`unconsented cookies: ${unconsentedCookies}`)
      }
    }
  }, [cookieDeclarations])

  // migrate legacy consent
  const [legacyConsentCookie] = useCookie('CookieInformationConsent')
  useEffect(() => {
    if (!consent && legacyConsentCookie) {
      const legacyConsent = decodeLegacyConsentCookie(legacyConsentCookie)
      if (legacyConsent) {
        persistConsent(legacyConsent)
      }
    }
  }, [persistConsent, consent, legacyConsentCookie])

  const isOpen = openState
    ? open
    : cookieConsentNeeded && !consent && !legacyConsentCookie

  return (
    <Dialog open={isOpen} modal={!disableBlur} onOpenChange={setOpen}>
      <DialogContent
        container={container}
        className="p-4 sm:p-5"
        style={{ zIndex: '2147483647' }}
      >
        <DialogHeader
          className="flex flex-col items-start gap-6"
          showCloseButton={Boolean(setOpen)}
        >
          <PlainBrandIcon icon={SvgLogoMain} className="shrink-0" height={32} />
          <DialogTitle>
            <Trans>We use cookies</Trans>
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="card gap-4 p-4 sm:p-6">
          {displaySettings ? (
            <Settings
              cookieDeclarations={cookieDeclarations}
              consent={customizedConsent}
              updateConsent={updateCustomizedConsent}
            />
          ) : (
            <MainPage />
          )}
        </DialogBody>

        <DialogFooter className="mt-6 flex w-full flex-col gap-3 sm:mt-8 sm:flex-row sm:gap-4">
          <button
            className="btn btn-elevated w-full sm:w-auto"
            disabled={isPending}
            onClick={() => setDisplaySettings(!displaySettings)}
          >
            {displaySettings ? (
              <Trans>Hide settings</Trans>
            ) : (
              <Trans>Change settings</Trans>
            )}
          </button>

          <div className="flex w-full grow flex-col justify-end gap-3 sm:w-auto sm:flex-row sm:gap-4">
            {displaySettings ? (
              <Button
                loading={isPending}
                className="btn btn-primary w-full sm:w-auto"
                onClick={() =>
                  persistConsent({
                    ...customizedConsent,
                  })
                }
              >
                <Trans>Save settings</Trans>
              </Button>
            ) : (
              <>
                <button
                  className="btn btn-elevated w-full sm:w-auto"
                  disabled={isPending}
                  onClick={() =>
                    persistConsent({
                      ...consent,
                      necessaryCookieConsent: true,
                      marketingCookieConsent: false,
                      statisticalCookieConsent: false,
                      functionalCookieConsent: false,
                    })
                  }
                >
                  <Trans>Reject unnecessary</Trans>
                </button>
                <button
                  className="btn btn-elevated w-full sm:w-auto"
                  disabled={isPending}
                  onClick={() =>
                    persistConsent({
                      ...consent,
                      marketingCookieConsent: true,
                      statisticalCookieConsent: true,
                      necessaryCookieConsent: true,
                      functionalCookieConsent: true,
                    })
                  }
                >
                  <Trans>Accept all</Trans>
                </button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
